import { Button, Input, Tabs, TabsContent, TabsList, TabsTrigger, cn } from "@autonoma/blacklight";
import { CircleNotchIcon } from "@phosphor-icons/react/CircleNotch";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/X";
import { type ReactNode, useEffect, useState } from "react";
import { type LogLevelFilter, BuildLogStreamViewer, buildPreviewLogStreamUrl } from "./build-log-stream-viewer";

// Wait for a pause in typing before applying the search, so each keystroke does not
// reopen the SSE stream (a filter change is a new server-side query from the cursor).
const SEARCH_DEBOUNCE_MS = 300;

const LEVEL_OPTIONS: Array<{ value: LogLevelFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "info", label: "Info" },
  { value: "error", label: "Error" },
];

/** Which log stream the tabs show: the build output or the running app's output. */
export type PreviewLogSource = "build" | "app";

interface PreviewLogsTabsProps {
  owner: string;
  repo: string;
  pr: number;
  /** When set, both tabs stream only this app's logs instead of the whole environment's. */
  app?: string | undefined;
  /** When true, the app is still building, so the App logs tab shows a placeholder (no runtime logs yet). */
  appBuilding?: boolean | undefined;
  /**
   * Rendered in the App logs tab when the runtime stream has nothing to show - for when the caller
   * knows why no output is coming and waiting is the wrong story (e.g. the preview has scaled to
   * zero). Runtime-only: build logs are a finished artifact, so their absence has a different cause.
   */
  appEmptyState?: ReactNode | undefined;
  /** When true, hide the Build logs tab and show only runtime output - for services not built from the PR (recipe pods). */
  runtimeOnly?: boolean | undefined;
  /** When true, the tabs grow to fill their flex parent (full-height layout) instead of a fixed body height. */
  fill?: boolean | undefined;
  /** Extra request headers, e.g. `{ Authorization: "Bearer <token>" }`. */
  headers?: Record<string, string> | undefined;
  /**
   * Token identifying the run being watched, forwarded to both streams. Changing it
   * drops what they have accumulated and reconnects. Pass it whenever the same
   * environment can deploy more than once while this stays mounted - otherwise the
   * viewer keeps showing the attempt that ended.
   */
  resetKey?: string | undefined;
  /**
   * When false, the viewers drop their own title/status header. For callers whose
   * surrounding layout already reports the deploy's status - two badges reading from
   * different sources is how a page ends up contradicting itself. Defaults to true.
   */
  viewerHeader?: boolean | undefined;
  /** Controls the active tab. When omitted, the tabs are uncontrolled and default to App logs. */
  source?: PreviewLogSource | undefined;
  /** Called when the user switches tabs - use it to persist the choice (e.g. in the URL). */
  onSourceChange?: ((source: PreviewLogSource) => void) | undefined;
  /**
   * Adds the level filter and a per-tab footer (status + line/error counts) - the "control room"
   * toolbar. Defaults to false, preserving the plain Build/App + search toolbar and per-viewer header
   * for the other pages that embed these tabs (onboarding, admin).
   */
  toolbar?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Logs for one preview environment as two tabs: the build output (Redis-backed
 * stream) and the running apps' stdout/stderr (Loki-backed, `?source=app`).
 * Radix only mounts the active tab's content, so each SSE stream opens on
 * demand and closes when the user switches away.
 *
 * Build logs lead (left) since they come first chronologically; callers that
 * control `source` follow the deploy - build while building, then app when ready.
 */
export function PreviewLogsTabs({
  owner,
  repo,
  pr,
  app,
  appBuilding,
  appEmptyState,
  runtimeOnly,
  fill,
  headers,
  resetKey,
  viewerHeader = true,
  source,
  onSourceChange,
  toolbar = false,
  className,
}: PreviewLogsTabsProps) {
  const contentClassName = fill === true ? "flex min-h-0 flex-col" : undefined;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [level, setLevel] = useState<LogLevelFilter>("all");

  // Debounce the search box: only the settled value drives the stream URL.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // The search hasn't taken effect yet while the debounce is still settling; show a
  // spinner in place of the search icon so the control's state is legible.
  const isSearchPending = search !== debouncedSearch;
  const filter = debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();
  const appWaitingText =
    filter != null ? `no application logs match “${filter}” yet…` : "waiting for application output…";
  const buildWaitingText = filter != null ? `no build logs match “${filter}” yet…` : undefined;

  return (
    <Tabs
      value={runtimeOnly === true ? "app" : source}
      defaultValue="app"
      onValueChange={(value) => onSourceChange?.(value === "build" ? "build" : "app")}
      className={cn("gap-2", fill === true && "min-h-0 flex-1", className)}
    >
      <div className={cn("flex items-center gap-3", toolbar && "flex-wrap pl-2 pt-2")}>
        <TabsList>
          {runtimeOnly !== true && <TabsTrigger value="build">Build logs</TabsTrigger>}
          <TabsTrigger value="app">App logs</TabsTrigger>
        </TabsList>

        {toolbar && (
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="font-mono text-3xs uppercase tracking-wider text-text-secondary">Level</span>
            {LEVEL_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={level === option.value ? "secondary" : "ghost"}
                size="xs"
                aria-pressed={level === option.value}
                onClick={() => setLevel(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}

        <div className={cn("relative max-w-xs", toolbar ? "min-w-40 flex-1" : "ml-auto w-full")}>
          {isSearchPending ? (
            <CircleNotchIcon
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 animate-spin text-text-secondary"
            />
          ) : (
            <MagnifyingGlassIcon
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
            />
          )}
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search logs…"
            aria-label="Search logs"
            className="h-8 pr-8 pl-8 font-mono text-2xs"
          />
          {search !== "" && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 -translate-y-1/2"
            >
              <XIcon size={12} />
            </Button>
          )}
        </div>
      </div>
      <TabsContent value="app" className={contentClassName}>
        {appBuilding === true ? (
          <AppLogsBuildingPlaceholder fill={fill} />
        ) : (
          <BuildLogStreamViewer
            url={buildPreviewLogStreamUrl(owner, repo, pr, "app", app, filter)}
            headers={headers}
            resetKey={resetKey}
            title="app logs"
            waitingText={appWaitingText}
            emptyState={appEmptyState}
            fill={fill}
            header={!toolbar && viewerHeader}
            footer={toolbar}
            levelFilter={toolbar ? level : undefined}
          />
        )}
      </TabsContent>
      {runtimeOnly !== true && (
        <TabsContent value="build" className={contentClassName}>
          <BuildLogStreamViewer
            url={buildPreviewLogStreamUrl(owner, repo, pr, "build", app, filter)}
            headers={headers}
            resetKey={resetKey}
            waitingText={buildWaitingText}
            fill={fill}
            header={!toolbar && viewerHeader}
            footer={toolbar}
            levelFilter={toolbar ? level : undefined}
          />
        </TabsContent>
      )}
    </Tabs>
  );
}

// Runtime (app) logs only exist once the container is running, so while the app is still building
// the App logs tab shows this instead of an indefinite "waiting for output" spinner.
function AppLogsBuildingPlaceholder({ fill }: { fill?: boolean | undefined }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 border border-border-dim bg-surface-void px-4 text-center",
        fill === true ? "min-h-0 flex-1" : "h-80",
      )}
    >
      <CircleNotchIcon className="size-4 animate-spin text-text-secondary" />
      <p className="font-mono text-2xs text-text-secondary">App logs will appear once the app finishes building.</p>
    </div>
  );
}
